import {StyleSheet, css} from 'aphrodite';
import React from 'react';
import {useSelector} from 'react-redux';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ComplexPolymerContentsQuery,
  ComplexPolymerContentsQueryVariables,
} from 'modbio/complex-polymers/__generated/ComplexPolymerContentsQuery';
import {
  COMPLEX_POLYMER_CONTENTS_QUERY,
  ComplexPolymerType,
  OligoConjugateVersionGraphQL,
  OligoDuplexVersionGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  isOligoConjugate,
  isOligoConjugateVersion,
  isOligoDuplex,
  isOligoDuplexVersion,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {MONOMER_QUERY} from 'molbio/monomers/MonomerConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {FONT_PROXIMA_19} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_DARKER} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';

import {transformStructureGraphForCanvas} from '../drag-and-drop/ComplexPolymerDragAndDropUtil';
import {
  OligoConjugateComponentsSummary,
  OligoDuplexComponentsSummary,
} from './ComplexPolymerComponentsSummary';
import ComplexPolymerComponentsTable from './ComplexPolymerComponentsTable';

export type ComplexPolymerStructure = OligoConjugateVersionGraphQL | OligoDuplexVersionGraphQL;

export interface ComplexPolymerComponentsTableTabProps {
  complexPolymerId: string;
  isEditable: boolean;
}

export default function ComplexPolymerComponentsTableTab({
  complexPolymerId,
  isEditable,
}: ComplexPolymerComponentsTableTabProps): JSX.Element | null {
  const openComplexPolymerVersionId = useSelector(
    (state: BenchlingState) =>
      state.ui.modbio.complexPolymerVersionUIs[complexPolymerId].complexPolymerVersionId
  );

  return (
    <>
      <Header1 className={css(styles.title)}>Polymer components</Header1>
      <BenchlingQuery<MonomerQuery>
        query={MONOMER_QUERY}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      >
        {({monomers}) => (
          <BenchlingQuery<ComplexPolymerContentsQuery, ComplexPolymerContentsQueryVariables>
            query={COMPLEX_POLYMER_CONTENTS_QUERY}
            renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
            variables={{complexPolymerId, versionId: openComplexPolymerVersionId}}
          >
            {(data) => {
              if (isInaccessible(data.complexPolymer)) {
                return (
                  <InlineBanner
                    bannerType={InlineBannerType.ERROR}
                    text="You do not have permission to view this complex polymer"
                  />
                );
              }

              if (!isOligoConjugate(data.complexPolymer) && !isOligoDuplex(data.complexPolymer)) {
                return (
                  <InlineBanner bannerType={InlineBannerType.ERROR} text="Entity is not a complex polymer" />
                );
              }

              assert(
                data.complexPolymer.currentVersion != null,
                'Expected complex polymer to have current version'
              );

              assert(data.complexPolymer.version != null, 'Expected complex polymer to have a version');
              const version = data.complexPolymer.version;

              assert(version.structure != null, 'Expected complex polymer to have non-null structure');

              const isLatest = openComplexPolymerVersionId === data.complexPolymer.currentVersion.id;

              if (isOligoConjugate(data.complexPolymer)) {
                assert(isOligoConjugateVersion(version), 'Expected oligo conjugate version to exist');
                if (!version.oligo) {
                  return (
                    <InlineBanner
                      bannerType={InlineBannerType.WARNING}
                      text="There is no associated oligo with this oligo-conjugate, please add an oligo"
                    />
                  );
                }

                return (
                  <div
                    {...getTestProps({
                      component: 'ComplexPolymerComponentsTab',
                      element: 'root',
                      key: ComplexPolymerType.OLIGO_CONJUGATE,
                    })}
                    data-amp-safe={false}
                  >
                    <OligoConjugateComponentsSummary
                      complexPolymerId={data.complexPolymer.id}
                      versionId={openComplexPolymerVersionId}
                      isEditable={isEditable && isLatest}
                      oligo={version.oligo}
                      attachedConjugates={version.conjugates}
                      simplePolymers={version.structure.polymers}
                      connections={version.structure.connections}
                      complexPolymerName={data.complexPolymer.name}
                      resolvedAttachments={version.resolvedAttachments}
                      structureGraph={transformStructureGraphForCanvas(
                        ComplexPolymerType.OLIGO_CONJUGATE,
                        version.structureGraph
                      )}
                    />
                    <ComplexPolymerComponentsTable
                      complexPolymerStructure={version}
                      // TODO (BNCH-90789) Enable animations on polymer map from interactions with table
                      complexPolymerId={complexPolymerId}
                      monomers={monomers}
                    />
                  </div>
                );
              } else {
                assert(isOligoDuplexVersion(version), 'Expected oligo duplex version to exist');
                assert(
                  version.forwardStrand != null && version.reverseStrand != null && version.offset != null,
                  'Expected oligo duplex to have two strands and non-null offset'
                );

                return (
                  <div
                    {...getTestProps({
                      component: 'ComplexPolymerComponentsTab',
                      element: 'root',
                      key: ComplexPolymerType.OLIGO_DUPLEX,
                    })}
                    data-amp-safe={false}
                  >
                    <OligoDuplexComponentsSummary
                      complexPolymerId={data.complexPolymer.id}
                      versionId={openComplexPolymerVersionId}
                      isEditable={isEditable && isLatest}
                      forwardStrand={version.forwardStrand}
                      reverseStrand={version.reverseStrand}
                      offset={version.offset}
                      simplePolymers={version.structure.polymers}
                      connections={version.structure.connections}
                      complexPolymerName={data.complexPolymer.name}
                      structureGraph={transformStructureGraphForCanvas(
                        ComplexPolymerType.OLIGO_DUPLEX,
                        version.structureGraph
                      )}
                    />
                    <div>
                      <ComplexPolymerComponentsTable
                        complexPolymerStructure={version}
                        // TODO (BNCH-90789) Enable animations on polymer map from interactions with table
                        complexPolymerId={complexPolymerId}
                        monomers={monomers}
                      />
                    </div>
                  </div>
                );
              }
            }}
          </BenchlingQuery>
        )}
      </BenchlingQuery>
    </>
  );
}

const styles = StyleSheet.create({
  title: {
    ...FONT_PROXIMA_19,
    color: GRAY_DARKER,
  },
});
